import React, { useState, useRef, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useClickOutside } from 'hooks/useClickOutside';
import CheckIcon from 'components/Icons/CheckIcon';
import styles from './Menu.module.css';
import Arrow from 'components/Icons/CaretIcon';

const MENU_DEFAULTS = {
    WIDTH: '200px',
    HOVER_DELAY: 300,
    SUBMENU_OFFSET: 1,
    Z_INDEX: {
        MENU: 1001,
        SUBMENU: 1002,
    },
} as const;

interface Position {
    readonly top: number;
    readonly left: number;
}

interface SubmenuState {
    readonly [key: string]: boolean;
}

interface SubmenuPositions {
    readonly [key: string]: Position;
}

const generateSubmenuId = () => `submenu-${Math.random().toString(36).substr(2, 9)}`;

const calculateSubmenuPosition = (triggerRect: DOMRect, offset: number = MENU_DEFAULTS.SUBMENU_OFFSET): Position => ({
    top: triggerRect.top,
    left: triggerRect.right + offset,
});

const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
};

interface MenuBaseProps {
    trigger: ReactNode;
    children: ReactNode;
    className?: string;
}

interface MenuControlProps {
    open?: boolean;
    onToggle?: (open: boolean) => void;
}

interface MenuStyleProps {
    width?: string;
    maxHeight?: string;
    triggerClassName?: string;
    menuClassName?: string;
}

interface MenuBehaviorProps {
    openOnHover?: boolean;
    hoverDelay?: number;
}

export interface MenuProps extends MenuBaseProps, MenuControlProps, MenuStyleProps, MenuBehaviorProps {}

export interface MenuItemProps {
    children: ReactNode;
    icon?: ReactNode;
    selected?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    className?: string;
}

export interface MenuHeaderProps {
    children: ReactNode;
    icon?: ReactNode;
    subtext?: string;
    className?: string;
}

export interface SubmenuProps {
    trigger: ReactNode;
    children: ReactNode;
    icon?: ReactNode;
    className?: string;
}

const MenuContext = React.createContext<{
    closeMenu: () => void;
    submenuStates: SubmenuState;
    setSubmenuState: (id: string, open: boolean) => void;
    submenuPositions: SubmenuPositions;
    setSubmenuPosition: (id: string, position: Position) => void;
} | null>(null);

const useMenuContext = () => {
    const context = React.useContext(MenuContext);
    if (!context) {
        throw new Error('useMenuContext must be used within a Menu component');
    }
    return context;
};

const useHoverDelay = (callback: () => void, delay: number = MENU_DEFAULTS.HOVER_DELAY) => {
    const timeoutRef = useRef<NodeJS.Timeout>();

    const trigger = useCallback(() => {
        timeoutRef.current = setTimeout(callback, delay);
    }, [callback, delay]);

    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { trigger, cancel };
};

const useScrollHandler = (callback: () => void, isActive: boolean) => {
    useEffect(() => {
        if (!isActive) return;

        const handleScroll = () => callback();

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [callback, isActive]);
};

const useSubmenuPosition = (submenuId: string, triggerRef: React.RefObject<HTMLDivElement>) => {
    const { submenuStates, setSubmenuPosition } = useMenuContext();

    const updatePosition = useCallback(() => {
        if (triggerRef.current && submenuStates[submenuId]) {
            const rect = triggerRef.current.getBoundingClientRect();
            setSubmenuPosition(submenuId, calculateSubmenuPosition(rect));
        }
    }, [submenuStates, submenuId, setSubmenuPosition, triggerRef]);

    return updatePosition;
};

export function Menu({
    trigger,
    children,
    open: controlledOpen,
    onToggle,
    width = MENU_DEFAULTS.WIDTH,
    maxHeight,
    className,
    triggerClassName,
    menuClassName,
    openOnHover = true,
    hoverDelay = MENU_DEFAULTS.HOVER_DELAY,
}: MenuProps) {
    const [internalOpen, setInternalOpen] = useState(false);
    const [submenuStates, setSubmenuStates] = useState<SubmenuState>({});
    const [submenuPositions, setSubmenuPositions] = useState<SubmenuPositions>({});
    const menuRef = useRef<HTMLDivElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout>();

    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const toggleMenu = useCallback(
        (open: boolean) => {
            if (isControlled) {
                onToggle?.(open);
            } else {
                setInternalOpen(open);
            }
        },
        [isControlled, onToggle]
    );

    const closeMenu = useCallback(() => {
        toggleMenu(false);
        setSubmenuStates({});
    }, [toggleMenu]);

    const setSubmenuState = useCallback((id: string, open: boolean) => {
        setSubmenuStates((prev) => ({ ...prev, [id]: open }));
    }, []);

    const setSubmenuPosition = useCallback((id: string, position: Position) => {
        setSubmenuPositions((prev) => ({ ...prev, [id]: position }));
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (!openOnHover) return;

        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        toggleMenu(true);
    }, [openOnHover, toggleMenu]);

    const handleMouseLeave = useCallback(() => {
        if (!openOnHover) return;

        hoverTimeoutRef.current = setTimeout(() => {
            toggleMenu(false);
        }, hoverDelay);
    }, [openOnHover, hoverDelay, toggleMenu]);

    const handleMenuMouseEnter = useCallback(() => {
        if (!openOnHover) return;

        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
    }, [openOnHover]);

    const handleMenuMouseLeave = useCallback(() => {
        if (!openOnHover) return;

        hoverTimeoutRef.current = setTimeout(() => {
            toggleMenu(false);
        }, hoverDelay);
    }, [openOnHover, hoverDelay, toggleMenu]);

    const handleClick = useCallback(() => {
        if (openOnHover) return;
        toggleMenu(!isOpen);
    }, [openOnHover, toggleMenu, isOpen]);

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const ref = useClickOutside(closeMenu);

    const contextValue = useMemo(
        () => ({
            closeMenu,
            submenuStates,
            setSubmenuState,
            submenuPositions,
            setSubmenuPosition,
        }),
        [closeMenu, submenuStates, setSubmenuState, submenuPositions, setSubmenuPosition]
    );

    const menuStyle = useMemo(
        () => ({
            width,
            maxHeight,
        }),
        [width, maxHeight]
    );

    return (
        <MenuContext.Provider value={contextValue}>
            <div
                ref={ref}
                className={`${styles.menuContainer} ${className || ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                role="menu"
                tabIndex={0}
            >
                <div className={`${styles.menuTrigger} ${triggerClassName || ''}`} onClick={handleClick}>
                    {trigger}
                </div>

                {isOpen && (
                    <div
                        ref={menuRef}
                        className={`${styles.menu} ${menuClassName || ''}`}
                        style={menuStyle}
                        onMouseEnter={handleMenuMouseEnter}
                        onMouseLeave={handleMenuMouseLeave}
                    >
                        {children}
                    </div>
                )}
            </div>
        </MenuContext.Provider>
    );
}

export function MenuHeader({ children, icon, subtext, className }: MenuHeaderProps) {
    return (
        <div
            className={`${styles.menuItem} ${styles.header} ${className || ''}`}
            onMouseEnter={stopPropagation}
            onMouseLeave={stopPropagation}
        >
            <div className={styles.menuItemContent}>
                {icon && <span className={styles.menuItemIcon}>{icon}</span>}
                <div className={styles.menuItemText}>
                    <span className={styles.menuItemLabel}>{children}</span>
                    {subtext && <span className={styles.menuItemSubtext}>{subtext}</span>}
                </div>
            </div>
        </div>
    );
}

export function MenuItem({ children, icon, selected, disabled, onClick, className }: MenuItemProps) {
    const { closeMenu } = useMenuContext();

    const handleClick = useCallback(() => {
        if (disabled) return;

        if (onClick) {
            onClick();
            closeMenu();
        }
    }, [disabled, onClick, closeMenu]);

    return (
        <div
            className={`${styles.menuItem} ${selected ? styles.selected : ''} ${disabled ? styles.disabled : ''} ${
                className || ''
            }`}
            onClick={handleClick}
            role="menuitem"
            tabIndex={0}
        >
            <div className={styles.menuItemContent}>
                {icon && <span className={styles.menuItemIcon}>{icon}</span>}
                <div className={styles.menuItemText}>
                    <span className={styles.menuItemLabel}>{children}</span>
                </div>
                {selected && <CheckIcon color="#000" />}
            </div>
        </div>
    );
}

export function Submenu({ trigger, children, icon, className }: SubmenuProps) {
    const { submenuStates, setSubmenuState, submenuPositions, setSubmenuPosition } = useMenuContext();
    const submenuId = useRef(generateSubmenuId()).current;
    const triggerRef = useRef<HTMLDivElement>(null);

    const updatePosition = useSubmenuPosition(submenuId, triggerRef);
    const hoverDelay = useHoverDelay(() => setSubmenuState(submenuId, false));

    useScrollHandler(updatePosition, submenuStates[submenuId]);

    const handleMouseEnter = useCallback(
        (event: React.MouseEvent) => {
            hoverDelay.cancel();

            const rect = event.currentTarget.getBoundingClientRect();
            setSubmenuPosition(submenuId, calculateSubmenuPosition(rect));

            setSubmenuState(submenuId, true);
        },
        [submenuId, setSubmenuState, setSubmenuPosition, hoverDelay]
    );

    const handleMouseLeave = useCallback(() => {
        hoverDelay.trigger();
    }, [hoverDelay]);

    const handleSubmenuMouseEnter = useCallback(() => {
        hoverDelay.cancel();
    }, [hoverDelay]);

    const handleSubmenuMouseLeave = useCallback(() => {
        hoverDelay.trigger();
    }, [hoverDelay]);

    const isOpen = submenuStates[submenuId];
    const position = submenuPositions[submenuId];

    return (
        <>
            <div
                ref={triggerRef}
                className={`${styles.menuItem} ${className || ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className={styles.menuItemContent}>
                    {icon && <span className={styles.menuItemIcon}>{icon}</span>}
                    <div className={styles.menuItemText}>
                        <span className={styles.menuItemLabel}>{trigger}</span>
                    </div>
                    <span className={styles.submenuArrow}>
                        <Arrow direction="right" color="#000" height="11" width="11" />
                    </span>
                </div>
            </div>

            {isOpen && position && (
                <div
                    className={styles.submenu}
                    style={{
                        position: 'fixed',
                        top: position.top,
                        left: position.left,
                        zIndex: MENU_DEFAULTS.Z_INDEX.SUBMENU,
                    }}
                    onMouseEnter={handleSubmenuMouseEnter}
                    onMouseLeave={handleSubmenuMouseLeave}
                >
                    {children}
                </div>
            )}
        </>
    );
}

export default Menu;
